import { useTranslation } from 'react-i18next'

import { Flex } from '@chakra-ui/react'

import { useReasonSubmit } from '@wwt/core/reason'
import { ReasonType } from '@wwt/entities/reason'
import PencilIcon from '@wwt/shared/assets/icons/outline/pencil-line.svg'
import InfoIcon from '@wwt/shared/assets/icons/outline/tip.svg'

import { useBreakpointValueWithoutSSR } from '@/shared/lib/hooks/useBreakpointValueWithoutSSR.ts'
import { useDesktop } from '@/shared/lib/hooks/useBreakpoints'

import {
	getQuickDislikeActions,
	getQuickLikeActions
} from '../config/quickActions.ts'
import { QuickActionMiniButton } from './QuickActionMiniButton'

interface QuickActionMiniSectionProps {
	onReviewPopupOpen: () => void
	onInfoPopupOpen: () => void
	type: ReasonType
	onClose: () => void
	offerId: string
}

export const QuickActionMiniSection = ({
	onReviewPopupOpen,
	onInfoPopupOpen,
	type,
	offerId,
	onClose
}: QuickActionMiniSectionProps) => {
	const { t } = useTranslation('tooltips')
	const submitReason = useReasonSubmit(type)

	const quickActions =
		type === 'like' ? getQuickLikeActions(t) : getQuickDislikeActions(t)

	const onQuickAction = (quickActionMessage: string) => {
		if (!offerId) {
			onClose()
			return
		}

		const data = {
			offerId,
			reason: [quickActionMessage]
		}

		submitReason(data)

		onClose()
	}

	const maxItems = useBreakpointValueWithoutSSR({ base: 3, xl: 10 })
	const isDesktop = useDesktop()
	return (
		<Flex
			w="100%"
			h="2.75rem"
			ps={{ base: 0, sm: '1.25rem' }}
			pe={{ base: 0, sm: '1.25rem' }}
			justifyContent="flex-start"
			alignItems="center"
		>
			{!isDesktop && (
				<QuickActionMiniButton
					data-wwt-id="reason-popup__open-info-modal--button"
					icon={InfoIcon}
					onClick={onInfoPopupOpen}
				/>
			)}
			{quickActions.slice(0, maxItems).map((action, i) => (
				<QuickActionMiniButton
					data-wwt-id="reason-popup__quick-action--button"
					// FIXME: index key
					// eslint-disable-next-line react/no-array-index-key
					key={i}
					isFirst={i === 0 && isDesktop}
					icon={action.quickActionIcon}
					tooltipInfo={{
						icon: action.tooltipIcon,
						title: action.tooltipTitle,
						description: action.tooltipDescription
					}}
					onClick={() => onQuickAction(action.tooltipTitle)}
				/>
			))}
			<QuickActionMiniButton
				data-wwt-id="reason-popup__open-review-modal--button"
				icon={PencilIcon}
				tooltipInfo={{
					icon: PencilIcon,
					title: t('reasonPopup.quickActions.leaveAReview'),
					description: t('reasonPopup.quickActions.giveUsYourFeedback')
				}}
				onClick={onReviewPopupOpen}
			/>
		</Flex>
	)
}
